import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { fetchGet } from '@par/shared/api/common';

type ActiveSubTabs =
  | 'dashboard'
  | 'questions-setup'
  | 'inaccuracies-report'
  | 'review-questions';

type PropTypes = {
  profile: Profile;
};

export const AdminSubNav = ({ profile }: PropTypes) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSubTab, setActiveSubTab] = useState<ActiveSubTabs>('dashboard');
  const [reportInaccuraciesCount, setReportInaccuraciesCount] =
    useState<number>();
  const [unreviewedQuestions, setUnreviewedQuestions] = useState<number>();

  useEffect(() => {
    fetchInaccuraciesReport();
    fetchUserSubmittedQuestions();
  }, []);

  const fetchInaccuraciesReport = async () => {
    const res = await fetchGet('/inaccuracy-report/all');
    setReportInaccuraciesCount(res.length);
  };

  const fetchUserSubmittedQuestions = async () => {
    const res = await fetchGet(`/custom-questions/all/${profile.id}/true`);
    setUnreviewedQuestions(res.length);
  };

  const SubHeaderTab = (isActiveTab: boolean, text: string) => (
    <Typography
      variant={isActiveTab ? 'subtitle2led' : 'subtitle2'}
      letterSpacing={2}
      color={!isActiveTab ? 'info.light' : null}
      sx={{
        filter: isActiveTab
          ? 'drop-shadow(0 0 15px #db3eb1) drop-shadow(0 0 50px #db3eb1) contrast(2)'
          : null,
      }}
    >
      {text}
    </Typography>
  );

  const handleAdminDashboardClick = () => navigate('/admin/dashboard');
  const handleQuestionsSetupClick = () => navigate('/admin/questions-setup');
  const handleInaccuraciesReportClick = () =>
    navigate('/admin/inaccuracies-report');
  const handleReviewQuestionsClick = () => navigate('/admin/review-questions');

  const handleSubNavChange = (event: any, tab: ActiveSubTabs) => {
    setActiveSubTab(tab);
    if (tab === 'dashboard') handleAdminDashboardClick();
    if (tab === 'questions-setup') handleQuestionsSetupClick();
    if (tab === 'inaccuracies-report') handleInaccuraciesReportClick();
    if (tab === 'review-questions') handleReviewQuestionsClick();
  };

  return (
    <Container>
      <Box display={'flex'} justifyContent={'left'} mt={2}>
        <Tabs
          variant="scrollable"
          value={false}
          onChange={handleSubNavChange}
          indicatorColor="secondary"
          sx={{
            width: '100%',
            height: 0,
            borderRadius: '10px',
            bgcolor: '#190c1a',
            '& .MuiTabs-flexContainer': { height: '100%' },
          }}
        >
          <Tab
            label={SubHeaderTab(activeSubTab === 'dashboard', 'Dashboard')}
            value="dashboard"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={SubHeaderTab(
              activeSubTab === 'questions-setup',
              'Questions Setup'
            )}
            value="questions-setup"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={
              <Badge badgeContent={reportInaccuraciesCount} color="error">
                {SubHeaderTab(
                  activeSubTab === 'inaccuracies-report',
                  'Inaccuracies'
                )}
              </Badge>
            }
            value="inaccuracies-report"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={
              <Badge badgeContent={unreviewedQuestions} color="success">
                {SubHeaderTab(
                  activeSubTab === 'review-questions',
                  'Review Questions'
                )}
              </Badge>
            }
            value="review-questions"
          />
        </Tabs>
      </Box>
    </Container>
  );
};
